/**
 * Search builder — one funnel step card: mode/match controls, condition rows
 * with inline editors, the three-way count strip (matched / excluded / no data,
 * each clickable into the drill-in), the unknown-data policy, and coverage +
 * warnings. Laptop-dense per the CRM density bar.
 */
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Pencil, Trash2, X } from 'lucide-react';
import type { CrmForm, SearchCondition, SearchPreviewStep, SearchStepDef, StudyOption, TeamMember } from '../../types';
import { fieldCls } from '../filters';
import CriterionEditor from './CriterionEditor';
import CriterionPalette from './CriterionPalette';
import { describeCondition, isConditionReady, WARNING_LABELS } from '../../pages/search/searchLib';

interface Props {
  step: SearchStepDef;
  stats: SearchPreviewStep | null;
  index: number;
  total: number;
  onChange: (next: SearchStepDef) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onDrill: (bucket: 'matched' | 'excluded' | 'no_data') => void;
  studies: StudyOption[];
  team: TeamMember[];
  forms: CrmForm[];
}

const tinySelectCls = `${fieldCls} w-auto cursor-pointer !py-1 text-xs`;

export default function StepCard({
  step, stats, index, total, onChange, onRemove, onMove, onDrill, studies, team, forms,
}: Props) {
  // Freshly-added incomplete conditions start expanded for editing.
  const [editing, setEditing] = useState<number | null>(() => {
    const firstIncomplete = step.conditions.findIndex((c) => !isConditionReady(c));
    return firstIncomplete >= 0 ? firstIncomplete : null;
  });
  const ctx = { studies, team, forms };

  const setCondition = (i: number, next: SearchCondition) =>
    onChange({ ...step, conditions: step.conditions.map((c, j) => (j === i ? next : c)) });
  const removeCondition = (i: number) => {
    const conditions = step.conditions.filter((_, j) => j !== i);
    setEditing(null);
    if (conditions.length === 0) onRemove();
    else onChange({ ...step, conditions });
  };
  const addCondition = (c: SearchCondition) => {
    onChange({ ...step, conditions: [...step.conditions, c] });
    setEditing(step.conditions.length);
  };

  const unknownControl = step.mode === 'include' ? (
    <select
      value={step.keep_unknown ? 'keep' : 'drop'}
      onChange={(e) => onChange({ ...step, keep_unknown: e.target.value === 'keep' || undefined })}
      aria-label="No-data policy"
      title="What happens to leads with NO data for this criterion"
      className={tinySelectCls}
    >
      <option value="drop">No data: excluded</option>
      <option value="keep">No data: kept</option>
    </select>
  ) : (
    <select
      value={step.drop_unknown ? 'drop' : 'keep'}
      onChange={(e) => onChange({ ...step, drop_unknown: e.target.value === 'drop' || undefined })}
      aria-label="No-data policy"
      title="What happens to leads with NO data for this criterion"
      className={tinySelectCls}
    >
      <option value="keep">No data: kept</option>
      <option value="drop">No data: excluded too</option>
    </select>
  );

  const showCoverage = stats && stats.coverage_pct < 100;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      {/* Header row */}
      <div className="flex items-center gap-1.5">
        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {index + 1}
        </span>
        <select
          value={step.mode}
          onChange={(e) => onChange({ ...step, mode: e.target.value as 'include' | 'exclude', keep_unknown: undefined, drop_unknown: undefined })}
          aria-label="Step mode"
          className={tinySelectCls}
        >
          <option value="include">Include</option>
          <option value="exclude">Exclude</option>
        </select>
        {step.conditions.length > 1 && (
          <select
            value={step.match}
            onChange={(e) => onChange({ ...step, match: e.target.value as 'any' | 'all' })}
            aria-label="Condition combination"
            title="How this step's conditions combine"
            className={tinySelectCls}
          >
            <option value="any">any of</option>
            <option value="all">all of</option>
          </select>
        )}
        <span className="min-w-0 flex-1" />
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move step up" className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move step down" className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
          <ChevronDown className="h-4 w-4" />
        </button>
        <button type="button" onClick={onRemove} aria-label="Remove step" className="rounded p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Condition rows */}
      <ul className="mt-2 space-y-1.5">
        {step.conditions.map((c, i) => (
          <li key={i} className="rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-gray-700/50">
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100" title={describeCondition(c, ctx)}>
                {i > 0 && <span className="mr-1 text-xs font-semibold text-gray-400">{step.match === 'all' ? 'AND' : 'OR'}</span>}
                {describeCondition(c, ctx)}
                {!isConditionReady(c) && <span className="ml-1.5 text-xs text-amber-600 dark:text-amber-400">incomplete</span>}
              </span>
              <button
                type="button"
                onClick={() => setEditing(editing === i ? null : i)}
                aria-label={editing === i ? 'Collapse editor' : 'Edit condition'}
                className={`rounded p-1 ${editing === i ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => removeCondition(i)} aria-label="Remove condition" className="rounded p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {editing === i && (
              <div className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-600">
                <CriterionEditor condition={c} onChange={(next) => setCondition(i, next)} studies={studies} team={team} forms={forms} />
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Footer: add-condition + unknown policy + counts */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <CriterionPalette compact label="Add condition" onPick={addCondition} />
        {unknownControl}
        {showCoverage && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              stats!.coverage_pct < 25
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
            title="Share of entering leads that have ANY data for this criterion"
          >
            {stats!.coverage_pct}% coverage
          </span>
        )}
      </div>

      {stats && (() => {
        // no-data leads pass on (include + keep) or (exclude + !drop) — the
        // excluded bucket counts only the with-data excluded otherwise.
        const noDataPassed = step.mode === 'include' ? Boolean(step.keep_unknown) : !step.drop_unknown;
        const excludedWithData = Math.max(0, stats.excluded - (noDataPassed ? 0 : stats.no_data));
        return (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2 text-xs dark:border-gray-700">
          <span className="tabular-nums text-gray-500 dark:text-gray-400">
            {stats.entering.toLocaleString()} in → <span className="font-semibold text-gray-800 dark:text-gray-100">{stats.passing.toLocaleString()}</span> pass
          </span>
          <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
          <button type="button" onClick={() => onDrill('matched')} className="rounded px-1.5 py-0.5 font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20">
            {stats.matched.toLocaleString()} matched
          </button>
          <button type="button" onClick={() => onDrill('excluded')} className="rounded px-1.5 py-0.5 font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20">
            {excludedWithData.toLocaleString()} excluded
          </button>
          <button type="button" onClick={() => onDrill('no_data')} className="rounded px-1.5 py-0.5 font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            {stats.no_data.toLocaleString()} no data
          </button>
          {stats.warnings.length > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" title={stats.warnings.map((w) => WARNING_LABELS[w] || w).join('\n')}>
              <AlertTriangle className="h-3.5 w-3.5" />
              {stats.warnings.length}
            </span>
          )}
        </div>
        );
      })()}
    </div>
  );
}
